import React from 'react';
import { Button } from "@/components/ui/Button";
import { ArrowRight, Globe, Smartphone, LineChart, Database, Building2, Factory, ShoppingBag, Car, Plane, Heart, GraduationCap, Camera, Users, MessageSquare, ChartLine } from "lucide-react";
import { PROJECTS } from '@/lib/constants';

const projects = [
  {
    title: "Application de Photos d'Identité",
    description: "Application mobile de photos d'identité officielles avec IA et vérification par des experts, générant des codes Ephoto agréés ANTS.",
    icon: Camera,
    features: [
      "Intelligence Artificielle",
      "Code Ephoto ANTS",
      "Paiements intégrés",
      "Vérification par experts",
      "Livraison rapide"
    ],
    priceRange: "À partir de 7 000€"
  },
  {
    title: "Marketplace C2C",
    description: "Plateforme de marketplace locale pour acheter et vendre des articles neufs ou d'occasion avec messagerie intégrée et options de livraison flexibles.",
    icon: ShoppingBag,
    features: [
      "Application mobile et web",
      "Messagerie intégrée",
      "Gestion des livraisons",
      "Système de notation",
      "Paiements sécurisés"
    ],
    priceRange: "À partir de 9 000€"
  },
  {
    title: "Plateforme d'Influence Marketing",
    description: "Solution connectant influenceurs et entreprises pour des collaborations marketing ciblées et efficaces.",
    icon: Users,
    features: [
      "Gestion des collaborations",
      "Paiements automatisés",
      "Carte intégrée",
      "QR Code unique",
      "Statistiques détaillées"
    ],
    priceRange: "À partir de 9 000€"
  },
  {
    title: "Plateforme de Crowdfunding Immobilier",
    description: "Solution de financement participatif immobilier permettant aux investisseurs de participer à des projets immobiliers avec un minimum d'investissement.",
    icon: Building2,
    features: [
      "Gestion des investissements",
      "Suivi des projets",
      "Paiements sécurisés",
      "Tableau de bord investisseur",
      "Rapports financiers"
    ],
    priceRange: "À partir de 18 000€"
  }
];

const industries = [
  {
    name: "Santé & Bien-être",
    icon: Heart,
    examples: ["Applications de suivi médical", "Plateformes de réservation", "Solutions de télémédecine"]
  },
  {
    name: "Éducation",
    icon: GraduationCap,
    examples: ["Plateformes d'apprentissage", "Gestion d'établissements", "Solutions de formation"]
  },
  {
    name: "Commerce & Retail",
    icon: ShoppingBag,
    examples: ["Solutions e-commerce", "Applications de fidélité", "Gestion de points de vente"]
  },
  {
    name: "Industrie",
    icon: Factory,
    examples: ["Suivi de production", "Gestion de maintenance", "Optimisation des processus"]
  },
  {
    name: "Transport & Logistique",
    icon: Car,
    examples: ["Suivi de flotte", "Optimisation des tournées", "Gestion de livraisons"]
  },
  {
    name: "Tourisme & Voyage",
    icon: Plane,
    examples: ["Réservations en ligne", "Applications de voyage", "Gestion d'établissements"]
  },
  {
    name: "Finance & Trading",
    icon: ChartLine,
    examples: ["Plateformes de trading", "Gestion de portefeuille", "Solutions de paiement"]
  },
  {
    name: "Médias & Communication",
    icon: MessageSquare,
    examples: ["Plateformes de contenu", "Solutions de streaming", "Outils de communication"]
  },
  {
    name: "Photographie & Design",
    icon: Camera,
    examples: ["Applications photo", "Outils de design", "Plateformes de partage"]
  }
];

const ProjectsSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Exemples de Projets
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Découvrez nos projets types et leurs fourchettes de prix pour mieux identifier les opportunités.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl border border-slate-200 p-4 sm:p-8 hover:border-orcsys-500 transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="bg-orcsys-50 p-3 rounded-lg self-start">
                  <project.icon className="h-6 w-6 text-orcsys-500" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                    <h3 className="text-xl font-semibold">
                      {project.title}
                    </h3>
                    <span className="text-orcsys-500 font-semibold">
                      {project.priceRange}
                    </span>
                  </div>
                  <p className="text-slate-600 mb-4">
                    {project.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {project.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-slate-600">
                        <span className="w-1.5 h-1.5 bg-orcsys-500 rounded-full mr-2"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mb-16">
          <Button 
            size="lg"
            className="bg-orcsys-500 hover:bg-orcsys-600 text-white"
            onClick={() => window.location.href = '/projets'}
          >
            Voir certains de nos projets
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <div className="bg-slate-50 rounded-xl p-8">
          <h3 className="text-2xl font-semibold text-center mb-4">
            Industries que nous accompagnons
          </h3>
          <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
            Notre expertise s'étend à de nombreux secteurs d'activité. Voici quelques exemples des industries que nous accompagnons régulièrement, mais nous sommes ouverts à tous les secteurs.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-slate-200 p-6"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-orcsys-50 p-3 rounded-lg">
                    <industry.icon className="h-6 w-6 text-orcsys-500" />
                  </div>
                  <h4 className="text-lg font-semibold">
                    {industry.name}
                  </h4>
                </div>
                <ul className="space-y-2">
                  {industry.examples.map((example, idx) => (
                    <li key={idx} className="flex items-center text-slate-600">
                      <span className="w-1.5 h-1.5 bg-orcsys-500 rounded-full mr-2"></span>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-slate-600 text-center mt-8">
            Et bien d'autres encore ! Nous adaptons nos solutions aux besoins spécifiques de chaque entreprise.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection; 